import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Callout, CircularLoader, Column, ConfirmationModal, FileDropzone, Row, TextButton, Typography } from 'seagull';
import { Bulb, Import } from 'seagull/icons';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { BlockingErrorView } from './BlockingErrorView';
import { CLIENTS_IMPORT_TEMPLATE_CSV, CLIENTS_IMPORT_TEMPLATE_CSV_WITH_ROLE, CLIENTS_IMPORT_TEMPLATE_FILENAME } from './clientsImportTemplate';
import styles from './ImportClientsModal.module.scss';
import { actionsFor, GetImportClientsModalTID } from './ImportClientsModal.utils';
import { ResultView } from './ResultView/ResultView';
const MODAL_WIDTH = '560px';
export const ImportClientsModal = (param)=>{
    let { dismissModal, onSubmit, onExportClients, initialMode, testId } = param;
    const { t: texts } = useLocaleKeys('pages.importClients');
    const { ImportClientsModalTestIds: TID } = useMemo(()=>GetImportClientsModalTID(testId), [
        testId
    ]);
    const { browser: { downloadCsv } } = useIoContext();
    const { CRM_CONTACTS } = useFeatureFlags();
    const templateCsv = CRM_CONTACTS === true ? CLIENTS_IMPORT_TEMPLATE_CSV_WITH_ROLE : CLIENTS_IMPORT_TEMPLATE_CSV;
    const [mode, setMode] = useState(initialMode !== null && initialMode !== void 0 ? initialMode : {
        kind: 'upload'
    });
    const downloadTemplate = useCallback(()=>{
        downloadCsv(templateCsv, CLIENTS_IMPORT_TEMPLATE_FILENAME);
    }, [
        templateCsv
    ]);
    const backToUpload = useCallback(()=>setMode({
            kind: 'upload'
        }), []);
    const handleFileSelected = useCallback(async (file)=>{
        setMode({
            kind: 'loading'
        });
        const next = await onSubmit(file);
        setMode(next);
    }, [
        onSubmit
    ]);
    const modalActions = actionsFor(mode, {
        dismissModal,
        backToUpload,
        texts,
        testId
    });
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        testId: TID.Root(),
        width: MODAL_WIDTH,
        openBottomSheetOnMobile: true,
        textAlignCenter: false,
        title: texts.title(),
        primaryButtonLabel: modalActions.primaryLabel,
        primaryButtonTestId: modalActions.primaryTestId,
        onPrimaryButtonClick: modalActions.onPrimary,
        secondaryButtonLabel: modalActions.secondaryLabel,
        secondaryButtonTestId: modalActions.secondaryTestId,
        onSecondaryButtonClick: modalActions.onSecondary,
        onCloseButtonClick: dismissModal,
        bi: {
            primaryButton: {
                name: modalActions.primaryBi
            },
            secondaryButton: {
                name: modalActions.secondaryBi
            },
            closeButton: {
                name: 'import_clients_close_x'
            }
        },
        content: /*#__PURE__*/ _jsx(ModalBody, {
            mode: mode,
            texts: texts,
            testId: testId,
            onFileSelected: handleFileSelected,
            onDownloadTemplate: downloadTemplate,
            onExportClients: onExportClients
        })
    });
};
function ModalBody(param) {
    let { mode, texts, testId, onFileSelected, onDownloadTemplate, onExportClients } = param;
    const { ImportClientsModalTestIds: TID } = useMemo(()=>GetImportClientsModalTID(testId), [
        testId
    ]);
    if (mode.kind === 'result') {
        return /*#__PURE__*/ _jsx(ResultView, {
            response: mode.response,
            testId: TID.ResultView()
        });
    }
    if (mode.kind === 'blockingError') {
        return /*#__PURE__*/ _jsx(BlockingErrorView, {
            errorKind: mode.errorKind,
            testId: TID.BlockingErrorView(),
            onDownloadTemplate: onDownloadTemplate
        });
    }
    if (mode.kind === 'loading') {
        return /*#__PURE__*/ _jsx(Column, {
            alignItems: "center",
            justifyContent: "center",
            padding: 48,
            testId: TID.Loader(),
            children: /*#__PURE__*/ _jsx(CircularLoader, {})
        });
    }
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 16,
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        testId: TID.Description(),
                        children: texts.uploadModal.description()
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(TextButton, {
                            onClick: onDownloadTemplate,
                            bi: {
                                name: 'import_clients_download_template'
                            },
                            variant: "paragraph2",
                            skin: "primary",
                            underline: "none",
                            testId: TID.DownloadTemplateButton(),
                            children: texts.uploadModal.downloadTemplate()
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FileDropzone, {
                bi: {
                    name: 'import_clients_file'
                },
                accept: ".csv",
                testId: TID.Dropzone(),
                inputTestId: "".concat(TID.Dropzone(), "-input"),
                onFileSelected: onFileSelected,
                className: styles.dropzone,
                renderContent: ()=>/*#__PURE__*/ _jsxs(Column, {
                        gap: 8,
                        alignItems: "center",
                        padding: 32,
                        children: [
                            /*#__PURE__*/ _jsx(Column, {
                                className: styles.iconCircle,
                                alignItems: "center",
                                justifyContent: "center",
                                children: /*#__PURE__*/ _jsx(Import, {
                                    size: 20
                                })
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                weight: "bold",
                                skin: "primary",
                                children: texts.uploadModal.dropzonePlaceholder()
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(Callout, {
                skin: "standard",
                icon: /*#__PURE__*/ _jsx(Bulb, {}),
                withBorder: true,
                classes: {
                    wrapper: styles.tipWrapper
                },
                testId: TID.Tip(),
                children: /*#__PURE__*/ _jsxs(Typography, {
                    variant: "paragraph3",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            weight: "bold",
                            as: "span",
                            children: texts.uploadModal.tipPrefix()
                        }),
                        ' ',
                        texts.uploadModal.tipRest(),
                        onExportClients != null && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                ' ',
                                /*#__PURE__*/ _jsx(TextButton, {
                                    onClick: onExportClients,
                                    bi: {
                                        name: 'import_clients_export_csv'
                                    },
                                    variant: "paragraph3",
                                    skin: "primary",
                                    underline: "none",
                                    testId: TID.TipExportLink(),
                                    children: texts.uploadModal.tipExportLink()
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
